// UX108 (docs/build-briefs/UX108-refresh-keeps-the-screen.md, section 2.3). Non-sensitive typed drafts, kept in
// sessionStorage so a browser refresh does not wipe a half-typed form. No DOM: the storage is injected, so a plain
// node:test file can drive it (test/ui-screen-drafts.test.ts).
//
// The rules, each one enforced in code below and by a test:
//   - tab scoped: sessionStorage is gone when the tab closes, and the key carries the tenant and the signed in
//     person, so one person's draft is never offered to another;
//   - an allowlist first: only the draft names in DRAFT_SHAPES can be stored or read, and each has its own shape
//     check (known keys only, strings, numbers and booleans with size caps) applied on write AND on read, so
//     whatever else reaches the store, or a value edited by hand, is dropped, never partly used;
//   - a word list second: a name or key made of the words in FORBIDDEN_WORDS (a PIN, a passcode, a token, a
//     name, an address, a phone, an email, a birth date, a note ...) is refused even inside an allowed draft.
//     Keys are split into words at capitals and separators and compared whole, so "tipPresets" and "mapping" pass
//     and "guestName" does not. (The card words the scanner forbids in source are covered by the prefix "card";
//     check:no-card-data would catch a card field name added here, which is why the list stays short.);
//   - small: a draft over MAX_DRAFT_CHARS is refused (a draft is a few fields, never a document);
//   - cleared: clearAllDrafts on sign-out, a lost session and a failed resume; clearDraft when the thing is saved
//     or its sheet is closed.
// Server-saved data (ticket lines, saved counts) is never a draft. It comes back from the server.

import type { KeyValueStorage } from "./session.ts";

const KEY_PREFIX = "bms-ui-draft:";
export const MAX_DRAFT_CHARS = 8_000;

/** Words that mark a person's data or a secret. A key or name containing one is refused. */
const FORBIDDEN_WORDS: ReadonlySet<string> = new Set([
  "pin",
  "pins",
  "ssn",
  "zip",
  "dob",
  "birth",
  "name",
  "names",
  "address",
  "note",
  "notes",
  "digits",
]);
/** Words refused when a key word merely STARTS with one (passcodes, phonenumber, cardholder, emails, tokens). */
const FORBIDDEN_PREFIXES: readonly string[] = [
  "passcode",
  "passwd",
  "password",
  "token",
  "secret",
  "credential",
  "card",
  "phone",
  "email",
  "address",
  "birth",
];

function keyWords(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 0);
}

export function isForbiddenKey(key: string): boolean {
  return keyWords(key).some(
    (word) =>
      FORBIDDEN_WORDS.has(word) || FORBIDDEN_PREFIXES.some((prefix) => word.startsWith(prefix)),
  );
}

export interface DraftScope {
  readonly tenantId: string;
  readonly userId: string;
}

// ---- the shape of every draft there is ----

type Shape = (value: unknown) => unknown;

const ID = /^[A-Za-z0-9_-]{1,64}$/;

function rec(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}
function str(value: unknown, max: number): string | undefined {
  return typeof value === "string" && value.length <= max ? value : undefined;
}
function id(value: unknown): string | undefined {
  return typeof value === "string" && ID.test(value) ? value : undefined;
}
function all<T>(values: readonly (T | undefined)[]): T[] | undefined {
  return values.every((v) => v !== undefined) ? (values as T[]) : undefined;
}

const shapeTicketSheet: Shape = (value) => {
  const r = rec(value);
  const orderId = id(r?.["orderId"]);
  const sheet = r?.["sheet"];
  if (orderId === undefined) return undefined;
  if (sheet !== "lineEdit" && sheet !== "priceChange" && sheet !== "addGuest") return undefined;
  const orderLineId = r?.["orderLineId"] === undefined ? undefined : id(r["orderLineId"]);
  if (r?.["orderLineId"] !== undefined && orderLineId === undefined) return undefined;
  return orderLineId === undefined ? { orderId, sheet } : { orderId, sheet, orderLineId };
};

const shapeSettingsForm: Shape = (value) => {
  const r = rec(value);
  if (r === undefined) return undefined;
  const tips = r["tipPresets"];
  const tiers = r["shelfTiers"];
  if (!Array.isArray(tips) || tips.length > 12) return undefined;
  if (!tips.every((n) => typeof n === "number" && Number.isInteger(n) && n >= 0 && n <= 100_000)) {
    return undefined;
  }
  if (!Array.isArray(tiers) || tiers.length > 20) return undefined;
  const shelfTiers = all(
    tiers.map((tier) => {
      const t = rec(tier);
      const label = str(t?.["label"], 80);
      const price = str(t?.["price"], 20);
      return label === undefined || price === undefined ? undefined : { label, price };
    }),
  );
  const holdAmount = str(r["holdAmount"], 20);
  const holdIncrementThreshold = str(r["holdIncrementThreshold"], 20);
  const abandonedTabGratuity = str(r["abandonedTabGratuity"], 20);
  const standardPourOunces = str(r["standardPourOunces"], 10);
  const businessDayCutoff = str(r["businessDayCutoff"], 10);
  const businessDayTimezone = str(r["businessDayTimezone"], 80);
  const roundingMode = str(r["roundingMode"], 30);
  const pricingModel = str(r["pricingModel"], 30);
  const disclosure = r["abandonedTabGratuityDisclosure"];
  if (
    shelfTiers === undefined ||
    holdAmount === undefined ||
    holdIncrementThreshold === undefined ||
    abandonedTabGratuity === undefined ||
    standardPourOunces === undefined ||
    businessDayCutoff === undefined ||
    businessDayTimezone === undefined ||
    roundingMode === undefined ||
    pricingModel === undefined ||
    typeof disclosure !== "boolean"
  ) {
    return undefined;
  }
  return {
    tipPresets: tips,
    shelfTiers,
    holdAmount,
    holdIncrementThreshold,
    abandonedTabGratuity,
    abandonedTabGratuityDisclosure: disclosure,
    roundingMode,
    pricingModel,
    standardPourOunces,
    businessDayCutoff,
    businessDayTimezone,
  };
};

const shapePolicyForm: Shape = (value) => {
  const r = rec(value);
  const minLength = str(r?.["minLength"], 6);
  const maxLength = str(r?.["maxLength"], 6);
  const lockoutAttempts = str(r?.["lockoutAttempts"], 6);
  const lockoutMinutes = str(r?.["lockoutMinutes"], 6);
  const sessionInactivity = str(r?.["sessionInactivity"], 6);
  if (
    minLength === undefined ||
    maxLength === undefined ||
    lockoutAttempts === undefined ||
    lockoutMinutes === undefined ||
    sessionInactivity === undefined
  ) {
    return undefined;
  }
  return { minLength, maxLength, lockoutAttempts, lockoutMinutes, sessionInactivity };
};

const shapeReceiveStock: Shape = (value) => {
  const r = rec(value);
  const productId = id(r?.["productId"]);
  const quantity = str(r?.["quantity"], 20);
  const unit = str(r?.["unit"], 20);
  const cost = str(r?.["cost"], 20);
  const reference = str(r?.["reference"], 80);
  if (
    productId === undefined ||
    quantity === undefined ||
    unit === undefined ||
    cost === undefined ||
    reference === undefined
  ) {
    return undefined;
  }
  return { productId, quantity, unit, cost, reference };
};

const shapeReceiveOrder: Shape = (value) => {
  const r = rec(value);
  const orderId = id(r?.["orderId"]);
  const lines = r?.["lines"];
  if (orderId === undefined || !Array.isArray(lines) || lines.length > 200) return undefined;
  const parsed = all(
    lines.map((line) => {
      const l = rec(line);
      const lineId = id(l?.["lineId"]);
      const quantity = str(l?.["quantity"], 20);
      const unit = str(l?.["unit"], 20);
      const cost = str(l?.["cost"], 20);
      return lineId === undefined ||
        quantity === undefined ||
        unit === undefined ||
        cost === undefined
        ? undefined
        : { lineId, quantity, unit, cost };
    }),
  );
  return parsed === undefined ? undefined : { orderId, lines: parsed };
};

const shapeCountEntry: Shape = (value) => {
  const r = rec(value);
  const sessionId = id(r?.["sessionId"]);
  const productId = id(r?.["productId"]);
  const quantity = str(r?.["quantity"], 20);
  const unit = str(r?.["unit"], 20);
  if (
    sessionId === undefined ||
    productId === undefined ||
    quantity === undefined ||
    unit === undefined
  ) {
    return undefined;
  }
  return { sessionId, productId, quantity, unit };
};

/** Every draft there is, and nothing else can be stored. */
const DRAFT_SHAPES: Readonly<Record<string, Shape>> = {
  ticketSheet: shapeTicketSheet,
  settingsForm: shapeSettingsForm,
  peopleForm: shapePolicyForm,
  receiveStock: shapeReceiveStock,
  receiveOrder: shapeReceiveOrder,
  countEntry: shapeCountEntry,
};

export const DRAFT_NAMES: readonly string[] = Object.keys(DRAFT_SHAPES);

function keyFor(scope: DraftScope, name: string): string {
  return `${KEY_PREFIX}${scope.tenantId}:${scope.userId}:${name}`;
}

function hasForbiddenKey(value: unknown, depth = 0): boolean {
  if (depth > 6) return true;
  if (Array.isArray(value)) return value.some((item) => hasForbiddenKey(item, depth + 1));
  if (typeof value === "object" && value !== null) {
    return Object.entries(value as Record<string, unknown>).some(
      ([key, inner]) => isForbiddenKey(key) || hasForbiddenKey(inner, depth + 1),
    );
  }
  return false;
}

/** True when the draft was stored. False (and nothing stored) for a forbidden name or key, a draft that is
 * too large or cannot be serialised, or storage that refuses the write. */
export function saveDraft(
  scope: DraftScope,
  name: string,
  value: unknown,
  storage: KeyValueStorage = window.sessionStorage,
): boolean {
  const shape = Object.hasOwn(DRAFT_SHAPES, name) ? DRAFT_SHAPES[name] : undefined;
  if (shape === undefined || isForbiddenKey(name)) return false;
  const parsed = shape(value);
  if (parsed === undefined || hasForbiddenKey(parsed)) return false;
  let text: string;
  try {
    text = JSON.stringify(parsed);
  } catch {
    return false;
  }
  if (text === undefined || text.length > MAX_DRAFT_CHARS) return false;
  try {
    storage.setItem(keyFor(scope, name), text);
    return true;
  } catch {
    return false;
  }
}

export function loadDraft(
  scope: DraftScope,
  name: string,
  storage: KeyValueStorage = window.sessionStorage,
): unknown {
  const shape = Object.hasOwn(DRAFT_SHAPES, name) ? DRAFT_SHAPES[name] : undefined;
  if (shape === undefined) return undefined;
  try {
    const raw = storage.getItem(keyFor(scope, name));
    if (raw === null) return undefined;
    return shape(JSON.parse(raw) as unknown);
  } catch {
    return undefined;
  }
}

export function clearDraft(
  scope: DraftScope,
  name: string,
  storage: KeyValueStorage = window.sessionStorage,
): void {
  try {
    storage.removeItem(keyFor(scope, name));
  } catch {
    // Storage unavailable: there is nothing stored to clear.
  }
}

/** Every draft key in a real Storage, for the sign-out sweep. The injected shape has no key listing, so this takes
 * the browser's Storage directly. */
export function clearAllDrafts(storage: Storage = window.sessionStorage): void {
  try {
    const doomed: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key !== null && key.startsWith(KEY_PREFIX)) doomed.push(key);
    }
    for (const key of doomed) storage.removeItem(key);
  } catch {
    // Storage unavailable: there is nothing stored to clear.
  }
}

// Small typed readers, so a restore never trusts the shape of what it finds.

export function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

export function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}
